<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Bell, Info, AlertTriangle, CheckCircle2, Gavel } from 'lucide-vue-next'
import { useNotificationsStore } from '@/stores/notifications'
import type { AppNotification } from '@/services/notificationService'
import { useI18n } from '@/composables/useI18n'
import Dropdown from '@/components/ui/Dropdown.vue'

const router = useRouter()
const store = useNotificationsStore()
const { t } = useI18n()

const isOpen = ref(false)

watch(isOpen, (open) => {
  if (open) store.fetchNotifications()
})

function handleClick(item: (typeof store.notifications)[number]) {
  store.markRead(item.id)
  isOpen.value = false
  if (item.link) {
    router.push(item.link)
  }
}

// Icon tile colour per notification type (presentation only). 'outbid' gets the gold
// chip rather than the generic warning amber: on an auction it is the one notification
// the bidder must act on, so it has to be pickable out of the list at a glance.
function tileClass(type: AppNotification['type']) {
  switch (type) {
    case 'alert':
      return 'bg-error/10 text-error'
    case 'outbid':
      return 'bg-primary/20 text-onPrimaryContainer'
    case 'warning':
      return 'bg-warning/10 text-warning'
    case 'success':
      return 'bg-success/10 text-success'
    default:
      return 'bg-accent text-text-primary'
  }
}

onMounted(() => {
  store.fetchNotifications()
})
</script>

<template>
  <div class="relative shrink-0">
    <Dropdown
      v-model="isOpen"
      trigger="click"
      placement="bottom"
      align="end"
    >
      <template #trigger>
        <button
          type="button"
          class="relative rounded-lg bg-ink/10 p-2 text-ink transition hover:bg-ink/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
          :title="t('notificationBell.title')"
          :aria-label="t('notificationBell.title')"
        >
          <Bell class="h-4 w-4" />
          <span
            v-if="store.unreadCount > 0"
            class="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-live ring-2 ring-primary"
          />
        </button>
      </template>

      <div class="w-80 max-w-[85vw] overflow-hidden rounded-2xl border border-border bg-surface shadow-xl sm:w-96">
        <div class="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h4 class="inline-flex items-center gap-2 font-display text-[15px] font-semibold text-text-primary">
            <span>{{ t('notificationBell.title') }}</span>
            <span
              v-if="store.unreadCount > 0"
              class="rounded-full bg-primary/20 px-2 py-0.5 text-[11px] font-bold tabular text-onPrimaryContainer"
            >{{ store.unreadCount }}</span>
          </h4>
          <button
            v-if="store.unreadCount > 0"
            type="button"
            class="shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-text-secondary transition hover:bg-accent hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            @click="store.markAllRead"
          >
            {{ t('notificationBell.markAllRead') }}
          </button>
        </div>

        <div v-if="store.notifications.length === 0" class="m-3 rounded-card border border-dashed border-border px-6 py-8 text-center">
          <span class="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-text-muted">
            <Bell class="h-5 w-5" />
          </span>
          <p class="text-sm font-semibold text-text-primary">{{ t('notificationBell.empty') }}</p>
          <p class="mt-1 text-xs text-text-muted">{{ t('notificationBell.emptySubtitle') }}</p>
        </div>

        <div v-else class="max-h-80 overflow-y-auto overscroll-contain">
          <button
            v-for="item in store.notifications"
            :key="item.id"
            type="button"
            class="flex w-full gap-3 border-b border-border px-4 py-3 text-left transition last:border-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            :class="!item.read ? 'bg-primary/10' : ''"
            @click="handleClick(item)"
          >
            <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" :class="tileClass(item.type)">
              <Gavel v-if="item.type === 'outbid'" class="h-4 w-4" />
              <AlertTriangle v-else-if="item.type === 'alert' || item.type === 'warning'" class="h-4 w-4" />
              <CheckCircle2 v-else-if="item.type === 'success'" class="h-4 w-4" />
              <Info v-else class="h-4 w-4" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="flex items-center justify-between gap-2">
                <span class="truncate text-xs text-text-primary" :class="item.read ? 'font-semibold' : 'font-bold'">{{ item.title }}</span>
                <span class="shrink-0 text-[11px] tabular text-text-muted">{{ item.time }}</span>
              </span>
              <span class="mt-0.5 block text-[11px] leading-relaxed text-text-secondary">{{ item.message }}</span>
            </span>
          </button>
        </div>
      </div>
    </Dropdown>
  </div>
</template>
